import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import CustomInput from "../../common/customInput/customInput";
import CustomButton from "../../common/customButton/customButton";
import BackButton from "../../common/backButton/backButton";
import useCustomInput from "../../common/customInput/hook/useCustomInput";
import useParentRegistration from "./hook/useParentRegistration";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useSharedState } from "../../context/formsContext";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import "./parentRegistration.css";

const ParentRegistration = () => {
  const { value, setValue, loading } = useSharedState();
  const navigate = useNavigate();
  const location = useLocation();

  const [sessionCourseId, setSessionCourseId] = useState(null);

  const getCourseId = () => {
    const params = new URLSearchParams(window.location.search);
    const course = params.get("course");
    const hostname = window.location.hostname;

    // Check if course is explicitly set in URL or if domain is ksolgcse.co.uk
    if (course === "gcse" || hostname.includes("ksolgcse.co.uk")) {
      localStorage.setItem("gcse", "true");
      setSessionCourseId(true);
    }
  };

  useEffect(() => {
    getCourseId();
  }, []);

  useEffect(() => {
    getCourseId();
  }, [sessionCourseId]);

  // Removed setUrlPrefix - not used in API calls and was causing confusion
  // API calls use 'prefix' from context which is set to process.env.REACT_APP_BASE_URL || "https://ksol.co.uk"

  // Pre-fill email from free downloads account if available (run FIRST before clearing localStorage)
  useEffect(() => {
    // Check for email from free downloads registration (in sessionStorage or localStorage)
    // IMPORTANT: Check mentorEmail BEFORE it gets removed
    const pendingEmail = sessionStorage.getItem('pendingLoginEmail');
    const localEmail = localStorage.getItem('email');
    const mentorEmail = localStorage.getItem('mentorEmail'); // Get this before it's removed
    
    // Priority: sessionStorage email > mentorEmail (from login) > localStorage email > existing value.email
    let emailToUse = null;
    if (pendingEmail && pendingEmail.trim()) {
      emailToUse = pendingEmail.trim();
    } else if (mentorEmail && mentorEmail.trim()) {
      // Use mentorEmail from login response
      emailToUse = mentorEmail.trim();
    } else if (localEmail && localEmail.trim()) {
      // Try to parse if it's JSON stringified
      try {
        const parsed = JSON.parse(localEmail);
        emailToUse = typeof parsed === 'string' ? parsed.trim() : localEmail.trim();
      } catch (e) {
        emailToUse = localEmail.trim();
      }
    }
    
    // Pre-fill email if we found one and value.email is empty
    if (emailToUse && (!value.email || !value.email.trim())) {
      setValue((prevValue) => ({
        ...prevValue,
        email: emailToUse,
      }));
      // Store in localStorage for persistence
      localStorage.setItem('email', emailToUse);
    }
  }, [setValue, value.email]);

  useEffect(() => {
    // Don't clear localStorage completely - we need to preserve email from free downloads
    // Only clear specific items that shouldn't persist
    // NOTE: mentorEmail is removed AFTER we've used it in the useEffect above
    localStorage.removeItem("initalLoadComplete");
    localStorage.removeItem("isGcse");
    localStorage.removeItem("loginToken");
    localStorage.removeItem("mentorEmail");
    localStorage.removeItem("mentorId");
    localStorage.removeItem("previousPage");
    localStorage.removeItem("studentId");
    localStorage.removeItem("testData");
  }, []);

  useEffect(() => {
    setInfoClicked(false);
  }, [value.phoneNumber]);

  const {
    nextClick,
    handleInputChange,
    errorMessage,

    validateEmailExists,
  } = useCustomInput();

  const {
    getAddressLookup,
    getFullAddressLookup,
    handleChange,
    setInfoClicked,
    fullAddressData,
    handleInfoClicked,
    infoClicked,
    addressData,
    addressLookupFailed,
    addressLookupAttempted,
  } = useParentRegistration({ value, setValue });

  const shouldShowManualAddressFields =
    addressLookupFailed ||
    !!fullAddressData.line_1 ||
    !!fullAddressData.line_2 ||
    !!fullAddressData.town_or_city ||
    !!fullAddressData.postcode;

  useEffect(() => {
    if (value.addressLineOne) {
      getAddressLookup();
    }
  }, [value.addressLineOne]);

  useEffect(() => {
    if (fullAddressData) {
      setValue((prevValue) => ({
        ...prevValue,
        addressLineOne: fullAddressData.line_1 ? fullAddressData.line_1 : "",
        addressLineTwo: fullAddressData.line_2 ? fullAddressData.line_2 : "",
        city: fullAddressData.town_or_city ? fullAddressData.town_or_city : "",
        postcode: fullAddressData.postcode ? fullAddressData.postcode : "",
      }));
    }
  }, [fullAddressData]);

  if (loading) {
    return (
      <UniformLoader
        title="Loading..."
        message="Please wait while we load the registration form"
        subMessage=""
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Parent Registration | KSOL</title>
        <meta name="description" content="Register as a parent to create a full KSOL account and access comprehensive 11+ preparation resources for your child." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full min-h-screen flex flex-col items-center justify-start bg-image p-4">
      <div className="w-full max-w-[80%] flex flex-col items-center p-6 xl:p-8">
        <div className="flex w-full justify-center ">
          <div className="flex w-11/12 mb-4 gap-2 ">
            <div id="parent-select" className=" flex flex-col w-1/2 ">
              <div
                id="parent-box"
                className="w-full h-4 mb-2 cursor-pointer box-bg-border box-bg-color"
              ></div>
              <p>Step 1</p>
              <p class="font-bold input-tags">Parent Details</p>
            </div>
            <div id="student-select" className=" flex flex-col w-1/2 ">
              <div
                id="student-box"
                onClick={nextClick}
                className="cursor-pointer box-bg-border h-4 mb-2"
              ></div>
              <p>Step 2</p>
              <p className="font-bold input-tags">Student Details</p>
            </div>
          </div>
        </div>
        <div className="w-full flex flex justify-center md:justify-center">
          <div className="w-11/12 flex flex-wrap flex-col  md:flex-nowrap">
            <h3 className=" mb-4 mt-4">Parent/ Mentor Details</h3>
            <div className="flex flex-wrap w-full  md:grid grid-cols-2 gap-4">
              <div className="flex flex-col w-full gap-4 md:flex-row">
                <div className="flex w-2/5 flex-col">
                  <div className=" flex">
                    <p>
                      Title <span style={{ color: "red" }}>*</span>
                    </p>
                  </div>

                  <CustomInput
                    placeholder={""}
                    isTitleDropdown={true}
                    type={"title"}
                    inputType={"title"}
                    value={value.title}
                    onChange={(event) => handleInputChange(event, "title")}
                    errorMessage={errorMessage.title || ""}
                  />
                </div>
                <div className="flex flex-col w-full">
                  <div className="w-4/5">
                    <p>
                      First Name <span style={{ color: "red" }}>*</span>
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-full">
                      <CustomInput
                        name="firstName"
                        placeholder={""}
                        inputType={"firstName"}
                        autoComplete="given-name"
                        value={value.firstName}
                        onChange={(event) =>
                          handleInputChange(event, "firstName")
                        }
                        errorMessage={errorMessage.firstName || ""}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full">
                <p>
                  Last Name <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  name="lastName"
                  inputType={"lastName"}
                  autoComplete="family-name"
                  value={value.lastName}
                  placeholder={""}
                  onChange={(event) => handleInputChange(event, "lastName")}
                  errorMessage={errorMessage.lastName || ""}
                />
              </div>
              <div className="w-full">
                <div className="flex items-end">
                  <p>
                    Phone Number <span style={{ color: "red" }}>*</span>
                  </p>
                  <div className="ml-2 mb-1">
                    <FontAwesomeIcon
                      onClick={handleInfoClicked}
                      icon={faCircleInfo}
                      size="lg"
                      style={{ color: "blue" }}
                    />
                  </div>
                </div>
                {infoClicked && (
                  <p className="text-sm mb-2 mt-2 text-blue-600 ">
                    This allows tutors to get in touch with you.
                  </p>
                )}

                <CustomInput
                  type={"number"}
                  inputType={"phoneNumber"}
                  value={value.phoneNumber}
                  placeholder={""}
                  onChange={(event) => handleInputChange(event, "phoneNumber")}
                  errorMessage={errorMessage.phoneNumber || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  Email{" "}
                  <span style={{ fontSize: "14px", color: "red" }}>
                    (This will be your username) *
                  </span>
                </p>
                <CustomInput
                  type={"email"}
                  inputType={"email"}
                  autoComplete="email"
                  placeholder={""}
                  value={value.email}
                  onChange={(event) => handleInputChange(event, "email")}
                  errorMessage={errorMessage.email || ""}
                  // onBlur={validateEmailExists}
                />
              </div>
              <>
                <div className="w-full col-span-2 ">
                  <p>
                    Address <span style={{ color: "red" }}>*</span>
                  </p>
                  <CustomInput
                    isAddress={true}
                    inputType={"addressLineOne"}
                    addressData={addressData}
                    placeholder={"Start Typing..."}
                    value={value.addressLineOne}
                    handleChange={handleChange}
                    getFullAddressLookup={getFullAddressLookup}
                    onChange={(event) =>
                      handleInputChange(event, "addressLineOne")
                    }
                    errorMessage={errorMessage.addressLineOne || ""}
                  />
                </div>
                {addressLookupFailed && addressLookupAttempted && (
                  <div className="w-full col-span-2">
                    <p className="text-sm text-amber-700">
                      Address lookup unavailable. Please enter your address manually below.
                    </p>
                  </div>
                )}
                {shouldShowManualAddressFields && (
                  <div className="w-full col-span-2">
                    <p>
                      Address Line 2
                    </p>
                    <CustomInput
                      value={value.addressLineTwo}
                      onChange={(event, addressId) =>
                        handleInputChange(event, "addressLineTwo", addressId)
                      }
                      errorMessage={errorMessage.addressLineTwo || ""}
                    />
                  </div>
                )}

                {shouldShowManualAddressFields && (
                  <div className="w-full">
                    <p>
                      City <span style={{ color: "red" }}>*</span>
                    </p>
                    <CustomInput
                      value={value.city}
                      onChange={(event, addressId) =>
                        handleInputChange(event, "city", addressId)
                      }
                      errorMessage={errorMessage.city || ""}
                    />
                  </div>
                )}
                {shouldShowManualAddressFields && (
                  <div className="w-full">
                    <p>
                      Postcode <span style={{ color: "red" }}>*</span>
                    </p>
                    <CustomInput
                      type={"postcode"}
                      inputType={"postcode"}
                      placeholder={""}
                      value={value.postcode}
                      onChange={(event) => handleInputChange(event, "postcode")}
                      errorMessage={errorMessage.postcode || ""}
                    />
                  </div>
                )}
              </>

              <div className="w-full">
                <p>
                  Password <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type={"password"}
                  inputType={"password"}
                  placeholder={""}
                  value={value.password}
                  onChange={(event) => handleInputChange(event, "password")}
                  errorMessage={errorMessage.password || ""}
                />
              </div>
              <div className="w-full">
                <p>
                  Confirm Password <span style={{ color: "red" }}>*</span>
                </p>
                <CustomInput
                  type={"confirmedPassword"}
                  inputType="password"
                  placeholder={""}
                  value={value.confirmedPassword}
                  onChange={(event) =>
                    handleInputChange(event, "confirmedPassword")
                  }
                  errorMessage={errorMessage.confirmedPassword || ""}
                />
              </div>

              <div className="col-span-2 mt-4 mb-6">
                <CustomButton onClick={nextClick} text={"Next"} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ParentRegistration;
